import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getRoles,
  getCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
} from '../api/client.js';

const EVENT_TYPES = [
  { id: 'weekly_self_meeting', label: 'Weekly self-meeting' },
  { id: 'quarterly_full_review', label: 'Quarterly full review' },
  { id: 'financial_review', label: 'Financial review' },
  { id: 'business_plan_review', label: 'Business plan review' },
  { id: 'training_milestone', label: 'Training milestone' },
  { id: 'client_project_milestone', label: 'Client / project milestone' },
  { id: 'holiday_travel', label: 'Holiday / travel' },
  { id: 'investment_review', label: 'Investment review' },
  { id: 'personal_goal', label: 'Personal goal' },
  { id: 'other', label: 'Other' },
];

const EVENT_TYPE_LABEL = Object.fromEntries(EVENT_TYPES.map((t) => [t.id, t.label]));
const NEUTRAL_COLOUR = '#94A3B8';
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}
function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function startOfWeekMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}
function getMonthGrid(anchor) {
  const start = startOfWeekMonday(startOfMonth(anchor));
  const out = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(d);
  }
  return out;
}
function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function isSameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
function rollingWindow() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const from = addMonths(today, -6);
  const to = addMonths(today, 6);
  // Snap from to first of month, to to last of month for clean year view
  from.setDate(1);
  to.setDate(1);
  const toEnd = new Date(to.getFullYear(), to.getMonth() + 1, 0);
  return { from: formatDate(from), to: formatDate(toEnd), today };
}

export default function CalendarView() {
  const [roles, setRoles] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [view, setView] = useState('month');
  const [anchorDate, setAnchorDate] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return startOfMonth(t);
  });
  const [filterRoleIds, setFilterRoleIds] = useState(() => new Set()); // empty = all
  const [filterEventTypes, setFilterEventTypes] = useState(() => new Set()); // empty = all

  const [modal, setModal] = useState({ open: false });

  const reload = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const win = rollingWindow();
      const [rolesList, eventsList] = await Promise.all([
        getRoles(),
        getCalendarEvents({ from: win.from, to: win.to }),
      ]);
      setRoles([...rolesList].sort((a, b) => a.priority_order - b.priority_order));
      setEvents(eventsList);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const rolesById = useMemo(() => {
    const m = new Map();
    for (const r of roles) m.set(r.id, r);
    return m;
  }, [roles]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (filterRoleIds.size > 0) {
        if (e.role_id == null || !filterRoleIds.has(e.role_id)) return false;
      }
      if (filterEventTypes.size > 0 && !filterEventTypes.has(e.event_type)) return false;
      return true;
    });
  }, [events, filterRoleIds, filterEventTypes]);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const e of filteredEvents) {
      const key = e.start_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    // Sort within each date: pinned first, then by id
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (!!b.is_pinned - !!a.is_pinned !== 0) return !!b.is_pinned - !!a.is_pinned;
        return a.id - b.id;
      });
    }
    return map;
  }, [filteredEvents]);

  function toggleRoleFilter(roleId) {
    setFilterRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }
  function toggleEventTypeFilter(typeId) {
    setFilterEventTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  }

  function openCreate(dateStr) {
    setModal({
      open: true,
      mode: 'create',
      data: {
        title: '',
        event_type: 'weekly_self_meeting',
        role_id: '',
        start_date: dateStr || formatDate(new Date()),
        end_date: '',
        is_pinned: false,
        notes: '',
      },
    });
  }
  function openEdit(event) {
    setModal({
      open: true,
      mode: 'edit',
      originalId: event.id,
      data: {
        title: event.title || '',
        event_type: event.event_type || 'other',
        role_id: event.role_id ?? '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        is_pinned: !!event.is_pinned,
        notes: event.notes || '',
      },
    });
  }
  function closeModal() {
    setModal({ open: false });
  }

  async function saveModal(values) {
    const payload = {
      title: values.title.trim(),
      event_type: values.event_type,
      role_id: values.role_id === '' ? null : Number(values.role_id),
      start_date: values.start_date,
      end_date: values.end_date || null,
      is_pinned: values.is_pinned,
      notes: values.notes || null,
    };
    if (modal.mode === 'create') {
      const created = await createCalendarEvent(payload);
      if (created) setEvents((prev) => [...prev, created]);
    } else {
      const updated = await updateCalendarEvent(modal.originalId, payload);
      if (updated)
        setEvents((prev) => prev.map((e) => (e.id === modal.originalId ? updated : e)));
    }
    closeModal();
  }

  async function deleteEvent(id) {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteCalendarEvent(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      closeModal();
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  if (loading) return <p className="text-sm text-slate-500">Loading calendar…</p>;
  if (error)
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-700">
        Calendar failed to load: {error}{' '}
        <button onClick={reload} className="underline">retry</button>
      </div>
    );

  return (
    <div className="space-y-4">
      <FilterBar
        roles={roles}
        filterRoleIds={filterRoleIds}
        filterEventTypes={filterEventTypes}
        onToggleRole={toggleRoleFilter}
        onToggleEventType={toggleEventTypeFilter}
        onClearFilters={() => {
          setFilterRoleIds(new Set());
          setFilterEventTypes(new Set());
        }}
      />

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
          <div className="flex items-center gap-1">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded-md ${view === 'month' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setView('month')}
            >
              Month
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded-md ${view === 'year' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setView('year')}
            >
              Year (±6mo)
            </button>
          </div>

          {view === 'month' && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setAnchorDate((a) => addMonths(a, -1))}
                className="px-2.5 py-1 text-sm rounded-md hover:bg-slate-100"
              >
                ← Prev
              </button>
              <button
                type="button"
                onClick={() => {
                  const t = new Date();
                  t.setHours(0, 0, 0, 0);
                  setAnchorDate(startOfMonth(t));
                }}
                className="px-2.5 py-1 text-sm rounded-md hover:bg-slate-100"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setAnchorDate((a) => addMonths(a, 1))}
                className="px-2.5 py-1 text-sm rounded-md hover:bg-slate-100"
              >
                Next →
              </button>
              <div className="ml-3 text-sm font-medium text-slate-800">
                {MONTH_NAMES[anchorDate.getMonth()]} {anchorDate.getFullYear()}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => openCreate(formatDate(anchorDate))}
            className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
          >
            + Add event
          </button>
        </div>

        {view === 'month' ? (
          <MonthView
            anchorDate={anchorDate}
            eventsByDate={eventsByDate}
            rolesById={rolesById}
            onCreate={openCreate}
            onEdit={openEdit}
          />
        ) : (
          <YearView
            today={(() => { const t = new Date(); t.setHours(0,0,0,0); return t; })()}
            eventsByDate={eventsByDate}
            rolesById={rolesById}
            onJumpToMonth={(date) => {
              setAnchorDate(startOfMonth(date));
              setView('month');
            }}
          />
        )}
      </div>

      {modal.open && (
        <EventModal
          mode={modal.mode}
          initial={modal.data}
          roles={roles}
          onCancel={closeModal}
          onSave={saveModal}
          onDelete={modal.mode === 'edit' ? () => deleteEvent(modal.originalId) : null}
        />
      )}
    </div>
  );
}

function FilterBar({
  roles,
  filterRoleIds,
  filterEventTypes,
  onToggleRole,
  onToggleEventType,
  onClearFilters,
}) {
  const hasFilters = filterRoleIds.size > 0 || filterEventTypes.size > 0;
  return (
    <div className="bg-white rounded-2xl shadow p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1.5">
          Roles
        </div>
        <div className="flex flex-wrap gap-1.5">
          {roles.map((r) => {
            const active = filterRoleIds.has(r.id);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => onToggleRole(r.id)}
                className={`px-2.5 py-1 rounded-full text-xs border ${active ? 'text-white' : 'text-slate-700 bg-white hover:bg-slate-50'}`}
                style={
                  active
                    ? { backgroundColor: r.colour, borderColor: r.colour }
                    : { borderColor: r.colour + '88' }
                }
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-1.5"
                  style={{ backgroundColor: active ? '#fff' : r.colour }}
                />
                {r.short_name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-start gap-3">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1.5">
          Types
        </div>
        <div className="flex flex-wrap gap-1.5">
          {EVENT_TYPES.map((t) => {
            const active = filterEventTypes.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onToggleEventType(t.id)}
                className={`px-2.5 py-1 rounded-full text-xs border ${active ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-700 bg-white hover:bg-slate-50'}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      {hasFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="text-xs text-slate-500 hover:text-slate-800 underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

function MonthView({ anchorDate, eventsByDate, rolesById, onCreate, onEdit }) {
  const grid = useMemo(() => getMonthGrid(anchorDate), [anchorDate]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div>
      <div className="grid grid-cols-7 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b">
        {DOW_SHORT.map((d) => (
          <div key={d} className="px-2 py-1.5 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-fr">
        {grid.map((date) => {
          const inMonth = isSameMonth(date, anchorDate);
          const isToday = isSameDay(date, today);
          const key = formatDate(date);
          const dayEvents = eventsByDate.get(key) || [];
          const visible = dayEvents.slice(0, 3);
          const overflow = dayEvents.length - visible.length;
          return (
            <div
              key={key}
              className={`min-h-[88px] border-b border-r p-1 text-xs ${inMonth ? 'bg-white' : 'bg-slate-50/50 text-slate-400'} ${isToday ? 'ring-1 ring-inset ring-slate-900/30' : ''}`}
              onClick={(e) => {
                if (e.target === e.currentTarget) onCreate(key);
              }}
            >
              <div className={`flex items-center justify-between mb-0.5 ${isToday ? 'font-semibold text-slate-900' : ''}`}>
                <span>{date.getDate()}</span>
                {dayEvents.length === 0 && inMonth ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCreate(key); }}
                    className="opacity-0 hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-slate-700 px-1"
                    title="Add event"
                  >
                    +
                  </button>
                ) : null}
              </div>
              <div className="space-y-0.5">
                {visible.map((ev) => {
                  const role = ev.role_id ? rolesById.get(ev.role_id) : null;
                  const colour = ev.colour_override || role?.colour || NEUTRAL_COLOUR;
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onEdit(ev); }}
                      className="block w-full text-left truncate rounded px-1 py-0.5 hover:opacity-80"
                      style={{ backgroundColor: colour + '22', color: colour, borderLeft: `2px solid ${colour}` }}
                      title={`${ev.title}${role ? ' · ' + role.short_name : ''}${ev.is_pinned ? ' · pinned' : ''}`}
                    >
                      {ev.is_pinned ? '★ ' : ''}{ev.title}
                    </button>
                  );
                })}
                {overflow > 0 ? (
                  <div className="text-[10px] text-slate-500 pl-1">+{overflow} more</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ today, eventsByDate, rolesById, onJumpToMonth }) {
  const months = useMemo(() => {
    const out = [];
    const base = startOfMonth(today);
    for (let i = -6; i <= 6; i++) out.push(addMonths(base, i));
    return out;
  }, [today]);

  return (
    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {months.map((m) => (
        <MiniMonth
          key={`${m.getFullYear()}-${m.getMonth()}`}
          month={m}
          today={today}
          eventsByDate={eventsByDate}
          rolesById={rolesById}
          onClick={() => onJumpToMonth(m)}
        />
      ))}
    </div>
  );
}

function MiniMonth({ month, today, eventsByDate, rolesById, onClick }) {
  const grid = useMemo(() => getMonthGrid(month), [month]);
  const monthEventCount = useMemo(() => {
    let count = 0;
    for (const date of grid) {
      if (!isSameMonth(date, month)) continue;
      const evs = eventsByDate.get(formatDate(date)) || [];
      count += evs.length;
    }
    return count;
  }, [grid, eventsByDate, month]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-slate-200 rounded-lg p-2 hover:border-slate-400 hover:shadow-sm transition"
    >
      <div className="flex items-center justify-between mb-1.5 px-1">
        <span className="text-xs font-semibold text-slate-800">
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </span>
        <span className="text-[10px] text-slate-400">{monthEventCount} ev</span>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {DOW_SHORT.map((d) => (
          <div key={d} className="text-[9px] text-slate-400 text-center">{d[0]}</div>
        ))}
        {grid.map((date) => {
          const inMonth = isSameMonth(date, month);
          const isToday = isSameDay(date, today);
          const evs = inMonth ? eventsByDate.get(formatDate(date)) || [] : [];
          const top = evs[0] || null;
          const role = top?.role_id ? rolesById.get(top.role_id) : null;
          const colour = top?.colour_override || role?.colour || NEUTRAL_COLOUR;
          return (
            <div
              key={formatDate(date)}
              className={`aspect-square flex items-center justify-center text-[10px] rounded-sm ${inMonth ? 'text-slate-700' : 'text-slate-300'} ${isToday ? 'ring-1 ring-slate-900' : ''}`}
              style={top ? { backgroundColor: colour + '33', color: colour } : undefined}
              title={top ? `${top.title}${role ? ' · ' + role.short_name : ''}${evs.length > 1 ? ` (+${evs.length - 1} more)` : ''}` : ''}
            >
              {date.getDate()}
              {top?.is_pinned ? <span className="absolute text-[7px]">★</span> : null}
            </div>
          );
        })}
      </div>
    </button>
  );
}

function EventModal({ mode, initial, roles, onCancel, onSave, onDelete }) {
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(k, v) {
    setValues((prev) => ({ ...prev, [k]: v }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!values.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!values.start_date) {
      setError('Start date is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(values);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-3 overflow-y-auto">
      <form
        onSubmit={handleSave}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3 my-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">
            {mode === 'create' ? 'New event' : 'Edit event'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-700 text-sm"
          >
            Cancel
          </button>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Title</span>
          <input
            type="text"
            value={values.title}
            onChange={(e) => set('title', e.target.value)}
            className="input mt-1"
            placeholder="e.g. Quarterly review with LTC"
            autoFocus
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Start date</span>
            <input
              type="date"
              value={values.start_date}
              onChange={(e) => set('start_date', e.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">End date (optional)</span>
            <input
              type="date"
              value={values.end_date}
              onChange={(e) => set('end_date', e.target.value)}
              className="input mt-1"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Type</span>
          <select
            value={values.event_type}
            onChange={(e) => set('event_type', e.target.value)}
            className="input mt-1"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Role (optional)</span>
          <select
            value={values.role_id}
            onChange={(e) => set('role_id', e.target.value)}
            className="input mt-1"
          >
            <option value="">— None —</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Notes</span>
          <textarea
            value={values.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={3}
            className="input mt-1 resize-y"
          />
        </label>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={values.is_pinned}
            onChange={(e) => set('is_pinned', e.target.checked)}
            className="h-4 w-4"
          />
          Pin (show this event first in year view for that date)
        </label>

        {error && <div className="text-xs text-rose-600">{error}</div>}

        <div className="flex items-center justify-between pt-2 border-t">
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="text-sm text-rose-600 hover:text-rose-800"
            >
              Delete
            </button>
          ) : <span />}
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:bg-slate-400"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { EVENT_TYPE_LABEL };
